// 신호 런타임 — **예약을 거는 일이 아니라 살려 두는 일**
//
// 예약을 거는 코드는 발사대 스텝이 이미 만들었다(`signal.ts`). 그것은 한 번에 끝나는
// 일이라 상태를 들 이유가 없다. 이 파일이 지는 것은 그 뒤의 시간이다 — 사람이 대상을
// 바꾸고, 화면이 백그라운드로 밀리고, 폰이 자다 깨는 동안 **걸어 둔 예약 하나가 계속
// 정직한가.**
//
// ── 문이 하나뿐이다 ─────────────────────────────────────────────────────────
//
// 밖으로 난 문은 `update` 하나이고, 화면은 매 틱 그것을 부른다. 「골랐다」·「바꿨다」·
// 「시간이 흘렀다」를 각각의 문으로 받지 않는 이유는, 그렇게 나누면 **어느 문으로 들어왔느냐에
// 따라 상태가 달라지는 자리**가 생기기 때문이다. 이 파일이 막으려는 실패가 정확히 그
// 종류다(화면이 사실을 파생시키지 않고 붙들고 있는 것). 지금 무엇을 골랐고 지금 몇 시인가
// 둘만 주면 나머지는 여기서 파생된다.
//
// ── 왜 React 밖에 있는가 ────────────────────────────────────────────────────
//
// 이 층에서 가장 위험한 상황(기기가 자는 동안 오디오 시계만 멈춘다)을 브라우저 검증이
// 만들지 못한다. Playwright 는 오디오 시계를 임의로 멈춰 세울 수 없고, 자동화 브라우저는
// 오히려 백그라운드 스로틀링을 기본으로 꺼 버린다. 프레임워크에 붙지 않은 층으로 두어야
// 가짜 컨텍스트가 그 상황을 직접 만들 수 있다. `ClockSync` 가 평범한 클래스이고
// `use-shell-clock.ts` 가 그 위의 얇은 바인딩인 것과 같은 갈림이다.

import { SIGNAL_PROFILE, type SignalProfile } from './profile.ts'
import {
  armAudio,
  decideSignalRefresh,
  describeBlockedState,
  honestStartAtSec,
  planSignal,
  scheduleSignal,
  type AudioContextLike,
  type SignalBinding,
  type SignalResult,
} from './signal.ts'

/**
 * 이 브라우저의 오디오 컨텍스트를 하나 만든다. 없는 브라우저면 `null` 이다.
 *
 * 브라우저 타입을 그대로 쓰지 않고 신호 모듈이 정한 최소 인터페이스로 좁힌다. 그래야
 * 단위 검사가 가짜 컨텍스트로 **예약된 시각 자체**를 잴 수 있고, 같은 인터페이스를
 * `OfflineAudioContext` 도 만족해 기계 판정이 실제 예약 코드를 그대로 렌더할 수 있다.
 */
export function createAudioContext(): AudioContextLike | null {
  const w = globalThis as unknown as {
    AudioContext?: new () => unknown
    webkitAudioContext?: new () => unknown
  }
  const Ctor = w.AudioContext ?? w.webkitAudioContext
  if (Ctor === undefined) return null
  return new Ctor() as AudioContextLike
}

/** 지금 소리를 듣기로 한 줄. **선택 단위는 rally 다** — 같은 사람이 둘을 맡을 수 있다. */
export interface SignalTarget {
  readonly rallyId: string
  /** 서버 시계 위의 개시 시각. */
  readonly openMs: number
}

export interface SignalStatus {
  /**
   * ── `missed` 와 `passed` 를 가른 이유 ────────────────────────────────────
   *
   * 둘 다 「이 개시는 지나갔다」이지만 **일어난 일이 정반대다.** `missed` 는 소리를 걸지
   * 못한 것이고(늦게 골랐거나, 오디오가 잠겼거나, 기기가 자는 동안 예약이 어긋났다),
   * `passed` 는 **제때 울리고 나서** 그 순간이 지난 것이다.
   *
   * 한동안 둘이 `missed` 하나로 접혀 있었고 그 자리가 누락 수를 센다. 그래서 제때 울린
   * 신호도 개시 뒤에 어긋남 검사가 한 번 발화하면 누락으로 세졌다 — **판정 계약이 「신호
   * 누락 0건, 이것은 평균이 아니다」를 요구하는 바로 그 값**이라, 정상 동작이 지표를
   * 부풀리는 상태였다. 2026-08-16 개정 회차가 갈랐다.
   *
   * ── `muted` 가 왜 여섯째 값인가 ─────────────────────────────────────────
   *
   * 사람이 스스로 끈 상태다. 나머지 다섯과 **원인의 종류가 다르다.** 저것들은 앱이나
   * 브라우저나 시간이 만든 상태이고 이것은 사람이 만든 상태다. 그래서 화면이 할 말도
   * 다르다: 잠김은 「눌러서 푸세요」, 놓침은 「지나갔습니다」인데 이것은 **「당신이 껐고,
   * 다시 켜면 그때부터 울립니다」**이다.
   *
   * 별도 boolean 이 아니라 상태 유니온에 넣는 이유는 화면 쪽 갈림이 하나이기 때문이다.
   * 상태를 읽어 상자를 고르는 자리가 `signal-zone.tsx` 의 조기 반환 사슬 하나뿐이라,
   * 유니온을 늘리면 타입 검사가 그 사슬의 누락을 잡는다. boolean 으로 두면 이미 셋이
   * 얽힌 기본 갈래에 조건이 하나 더 붙고, 그때 「예약됨」과 「껐음」이 서로 가린다.
   */
  readonly state: 'idle' | 'locked' | 'scheduled' | 'missed' | 'passed' | 'muted'
  /** 소리가 안 나는 자리에서 **왜** 안 나는지. 비어 있으면 말할 것이 없다는 뜻이다. */
  readonly reason: string
  /** 구간 한복판에 들어와 놓친 표지 수. 조용히 적게 울리면 사람이 자기 탓을 한다. */
  readonly droppedMarkers: number
}

const IDLE: SignalStatus = { state: 'idle', reason: '', droppedMarkers: 0 }

/**
 * 마지막 스텝이 판정할 두 숫자. **여기서 재지 않으면 그때 잴 것이 없다.**
 *
 * 둘 다 **전이마다** 센다. 이 문이 초당 다섯 번 도는데 「지금 그 상태다」를 매번 세면
 * 5초 방치가 25건으로 찍히고, 판정 계약의 「신호 누락 0건 — 이것은 평균이 아니다」가
 * 판정할 수 없는 숫자 위에 서게 된다.
 */
export interface SignalObservations {
  /** 고른 rally 의 개시가 소리 없이 지나간 횟수. 판정 계약이 0을 요구한다. */
  readonly missedCount: number
  /** 어긋남을 발견해 다시 건 횟수. 자주 1 이상이면 임계를 올릴 근거가 된다. */
  readonly rescheduleCount: number
}

export interface SignalRuntimeOptions {
  context: AudioContextLike
  profile?: SignalProfile
}

/** 지금 걸려 있는 예약 하나. **둘이 될 수 없다** — 소리가 하나뿐인 것이 그래서 지켜진다. */
interface HeldReservation {
  readonly target: SignalTarget
  /** 무엇을 오디오 시계의 어디에 묶었는지. **이 짝이 없으면 어긋남을 잴 수 없다.** */
  readonly binding: SignalBinding
  readonly cancel: () => void
}

function sameTarget(a: SignalTarget, b: SignalTarget): boolean {
  // 개시 시각까지 함께 본다. 재구성 스텝이 시각만 옮기는 경우가 있어서, rally 만 견주면
  // 그때 옛 시각에 걸린 예약이 그대로 살아남는다.
  return a.rallyId === b.rallyId && a.openMs === b.openMs
}

export class SignalRuntime {
  readonly #ctx: AudioContextLike
  readonly #profile: SignalProfile

  #held: HeldReservation | null = null
  #status: SignalStatus = IDLE
  #missedCount = 0
  #rescheduleCount = 0
  /** 이미 누락으로 센 대상. 같은 것을 틱마다 다시 세지 않으려고 든다. */
  #countedMiss: SignalTarget | null = null
  /**
   * 그 자물쇠를 `#keepHonest` 의 지나감 갈래가 박았는가. **`#reserve` 가 푸는 것은 이 경우뿐이다**
   * (T6 개정 셋째, 2026-10-07). 끈 채 지난 개시(`update` 의 muted 갈래)와 이미 센 누락(`#missed`)의 자물쇠까지
   * 풀면, 시계가 개시 전으로 돌아왔다 다시 넘어갈 때 끈 구간이 누락이 되고 한 누락이 두 번 세진다(적대 리뷰가 재현).
   */
  #lockFromPassed = false
  /**
   * 사람이 소리를 껐는가. **새로고침을 넘기지 않는다** (정본 2026-09-02 개정). 저장소에
   * 남기지 않으므로 이 필드가 사는 동안만 참이고, 화면을 다시 열면 켜짐으로 돌아온다.
   */
  #muted = false

  constructor(options: SignalRuntimeOptions) {
    this.#ctx = options.context
    this.#profile = options.profile ?? SIGNAL_PROFILE
  }

  get status(): SignalStatus {
    return this.#status
  }

  get observations(): SignalObservations {
    return { missedCount: this.#missedCount, rescheduleCount: this.#rescheduleCount }
  }

  /**
   * 잠긴 오디오를 깨운다. **사람의 제스처 안에서 불러야 한다** — 그 밖에서 부르면
   * 브라우저가 풀어 주지 않는다. 세션 승계로 다시 붙었을 때도 한 번 더 필요하다.
   */
  async arm(): Promise<SignalStatus> {
    const armed = await armAudio(this.#ctx)
    if (armed.armed) return this.#status
    return this.#settle({ state: 'locked', reason: armed.reason, droppedMarkers: 0 })
  }

  get muted(): boolean {
    return this.#muted
  }

  /**
   * 사람이 소리를 끄거나 켠다. **`dispose` 로는 이 일을 할 수 없다.**
   *
   * `dispose` 는 들고 있는 것만 놓고 상태를 되돌리는데, 이 문은 초당 다섯 번 도는
   * `update` 가 **아무 일 없었다는 듯 다시 건다** (그 부활은 단위 검사가 계약으로
   * 붙들고 있고, React 의 StrictMode 가 붙였다 떼었다 하는 개발 환경이 그것을 요구한다).
   * 그래서 끄기는 「걷는 일」이 아니라 **「거는 일을 하지 않기로 하는 상태」**여야 한다.
   *
   * `ctx.suspend()` 도 답이 아니다. 그것은 `ctx.state` 를 iOS 잠김과 **같은 값**으로
   * 만들고, 화면은 그 값을 읽어 「오디오가 잠겨 있습니다, 눌러서 푸세요」라고 말한다.
   * 사람이 스스로 끈 것을 브라우저가 막은 것처럼 설명하는 셈이라 원인을 잘못 가리킨다.
   *
   * **끌 때 걸린 예약을 걷는다.** 안 걷으면 이미 오디오 시계에 올라간 표지와 출발음이
   * 그대로 울어, 껐다고 말하는 화면 앞에서 소리가 난다.
   */
  setMuted(next: boolean): SignalStatus {
    if (this.#muted === next) return this.#status
    this.#muted = next
    if (!next) return this.#status
    this.#release()
    return this.#settle({ state: 'muted', reason: '', droppedMarkers: 0 })
  }

  update(input: { target: SignalTarget | null; serverNowMs: number }): SignalStatus {
    // 고른 것을 끄면 소리도 함께 멎는다. 선택 모듈에 「끄는 문」이 따로 없고 고른 줄을
    // 다시 누르는 것이 곧 끄는 것이라, 여기서 걷지 않으면 **끌 수 없는 소리**가 남는다.
    if (input.target === null) {
      this.#release()
      return this.#settle(IDLE)
    }

    // **껐으면 여기서 멈춘다.** 아래 `#reserve` 가 누락을 세는 유일한 자리라, 거기 닿지
    // 않게 하는 것만으로 「사람이 끈 구간은 누락이 아니다」가 지켜진다 (정본 2026-09-02
    // 개정). 계수 로직 자체는 한 줄도 건드리지 않는다.
    //
    // **`#countedMiss` 를 매 틱 지금 대상으로 박아 두는 것이 요점이다.** 안 그러면 다시
    // 켜는 첫 틱이 `#reserve` 로 들어가고, 그 사이 개시가 지나 있었으면 `planSignal` 이
    // too-late 를 내 **사람이 꺼 둔 구간의 개시가 앱의 실패로 한 건 찍힌다.** 판정 계약이
    // 요구하는 「누락 0건」은 평균이 아니라 한 번이라도 나면 불통이라, 그 한 건이 비싸다.
    // 자물쇠를 미리 박아 두면 해제 첫 틱의 `#missed` 가 「이미 센 것」으로 보고 넘어간다.
    if (this.#muted) {
      this.#release()
      // **자물쇠는 개시가 이미 지난 뒤에만 박는다.** 조건 없이 박으면 개시 한참 전에 잠깐
      // 껐다 켠 사람의 대상이 그 뒤로 영구히 면제되어, **소리를 켜 둔 채 일어난 진짜
      // 누락**이 `missedCount` 에 안 잡힌다. 여기서 박은 자물쇠는 `#missed` 가 다른 대상으로 덮어쓸
      // 때까지 남고(`#reserve` 는 지나감 갈래가 박은 것만 푼다), 그 문은 이미 잠긴 대상을 세지 않기 때문이다. 그러면 「누락 0건」은 토글을 한
      // 번이라도 만진 사람에게 언제나 참인, 판정할 수 없는 값이 된다.
      // (2026-09-04 독립 반증이 잡았다. 다섯 렌즈가 서로 모르는 채 같은 자리를 찾았고
      // 반증자 다섯이 전부 반증에 실패했다. 게이트는 초록이었다.)
      //
      // **이 부등식이 `planSignal` 의 too-late 경계와 같은 말이라는 것이 요점이다.**
      // `honestStartAtSec` 이 `audioNowSec + (openMs - serverNowMs) / 1000` 이므로
      // 그쪽의 `startAtSec <= audioNowSec` 는 `serverNowMs >= openMs` 로 그대로 옮겨진다.
      // 그래서 여기서 박는 경우와 해제 첫 틱이 too-late 를 내는 경우가 어긋나지 않는다.
      // 조건을 눈대중으로 고른 것이 아니라 **같은 부등식을 옮겨 적은 것**이다.
      //
      // 남는 틈 하나는 알고 남긴다: 개시를 껐던 상태로 지나보내고 **그 뒤 0.2초 안에**
      // 다시 켜면, 마지막 muted 틱이 개시 전이라 자물쇠가 안 박혀 한 건이 찍힌다. 틱이
      // 0.2초 간격이라 그 사람은 자기 개시 순간에 손잡이를 누르고 있어야 한다. 그리고 이
      // 틈은 **안전한 쪽으로 틀린다**: 없는 누락을 세면 게이트가 빨개져 사람이 들여다보고,
      // 있는 누락을 안 세면 지표가 조용히 눈이 먼다. 위 결함이 뒤쪽이었다.
      if (input.serverNowMs >= input.target.openMs) {
        this.#countedMiss = input.target
        this.#lockFromPassed = false
      }
      return this.#settle({ state: 'muted', reason: '', droppedMarkers: 0 })
    }

    // 고른 것이 그대로면 **새로 걸지 않는다** — 화면이 이 문을 매 틱 부르므로 매번 다시
    // 걸면 초당 다섯 겹으로 소리가 쌓인다. 그러나 「아무 일도 하지 않는다」는 아니다:
    // 고른 것이 안 바뀌어도 **세상 쪽이 바뀔 수 있다.**
    const held = this.#held
    if (held !== null && sameTarget(held.target, input.target)) {
      return this.#keepHonest(held, input.serverNowMs)
    }

    return this.#reserve(input.target, input.serverNowMs)
  }

  /**
   * 걸어 둔 예약이 아직 정직한가. **이 스텝이 지키기로 한 것이 이 함수 하나다.**
   *
   * 기기가 자면 오디오 시계가 멈추고 예약은 취소되는 것이 아니라 늦게 울린다. 시계 동기가
   * 다시 수렴하면 예약된 값에 박힌 옛 offset 이 틀려진다. 어느 쪽이든 **표지 다섯과
   * 출발음은 등간격을 완벽히 유지한 채** 통째로 옮겨 가고, 그것을 들은 사람은 틀린 시각을
   * 확신하며 누른다. 소리가 안 나는 것보다 나쁘다.
   */
  #keepHonest(held: HeldReservation, serverNowMs: number): SignalStatus {
    const refresh = decideSignalRefresh({
      binding: held.binding,
      audioNowSec: this.#ctx.currentTime,
      serverNowMs,
      audioState: this.#ctx.state,
    })

    if (refresh.kind === 'keep') {
      // **개시가 지나면 그 순간 말이 바뀐다.** 여기 오기 전까지 화면은 캐시된 `scheduled`
      // 를 그대로 받고 있었고, 상태가 바뀌는 것은 어긋남 검사가 우연히 발화할 때뿐이라
      // **지나감 상자가 6~17초 늦게 떴다.** 소리가 풀린 화면에서만 그랬던 것이 지문이다 —
      // 잠긴 화면은 예약을 들고 있지 않아 매 틱 처음부터 다시 계산한다.
      // (2026-08-14 대리 관측이 적었고 2026-08-16 개정 회차가 닫았다.)
      //
      // **이 갈래가 `passed` 인 것이 요점이다.** `keep` 은 예약이 정직했다는 뜻이고, 정직한
      // 예약은 제때 울렸다. 못 울린 경우는 어긋남이 잡혀 아래 `reschedule` 로 가고 거기서
      // `missed` 가 되어 **누락으로 세진다.** 두 갈래를 가르지 않으면 정상 동작이 판정
      // 계약의 「누락 0건」을 부풀린다.
      if (serverNowMs > held.target.openMs) {
        // **제때 울린 이 개시는 같은 대상을 고른 채 개시 뒤에 머무는 동안 누락이 되지 않는다** (T6 개정 셋째, 2026-10-05).
        // 이 문이 계속 돌다 시계 보정이나 잠김으로 다시 걸려다 늦었다는 판정을 받으면 `#missed` 가 이미 울린 개시를 한 건으로
        // 셌다(적대 리뷰가 재현). 자물쇠를 이 대상에 박아 두면 그 판정이 「이미 센 것」으로 넘어간다. 다시 잡은 새 개시는
        // 개시 시각이 달라 다른 대상이므로 따로 센다. 알고 남긴 틈 둘: 다른 대상을 울린 뒤 이 대상을 다시 고르면(A, B, A)
        // 자물쇠가 B 에 있어 한 건이 찍히고, 시계가 개시 전으로 되돌아가면 `#reserve` 가 이 자물쇠를 풀어 재동기 때 한 건이
        // 찍힌다. 둘 다 없는 누락을 세는 쪽, 곧 게이트가 빨개지는 안전한 쪽으로 틀린다.
        this.#countedMiss = held.target
        this.#lockFromPassed = true
        return this.#settle({ state: 'passed', reason: '', droppedMarkers: 0 })
      }
      return this.#status
    }

    // 잠긴 채로는 다시 걸지 않는다 — 멈춘 시계에 또 거는 일이다. 다만 어긋난 예약은
    // 걷는다: 살려 두면 잠김이 풀리는 순간 그것이 틀린 자리에서 운다.
    if (refresh.kind === 'locked') {
      this.#release()
      return this.#settle({ state: 'locked', reason: refresh.reason, droppedMarkers: 0 })
    }

    this.#rescheduleCount += 1
    return this.#reserve(held.target, serverNowMs)
  }

  /**
   * 걸려 있던 것을 걷고 새로 건다. **걷는 것이 먼저다** — 순서가 뒤집히면 두 예약이 잠깐
   * 겹치고, 그 잠깐이 하필 구간 안이면 사람이 소리를 둘 듣는다.
   */
  #reserve(target: SignalTarget, serverNowMs: number): SignalStatus {
    this.#release()

    const startAtSec = honestStartAtSec(target.openMs, {
      audioNowSec: this.#ctx.currentTime,
      serverNowMs,
    })

    // **지나간 개시가 잠긴 오디오를 이긴다 — 순서가 이 자리의 전부다.**
    //
    // 잠김을 먼저 보면, 개시가 이미 지난 사람에게 화면이 「소리를 켜세요」라고 답한다.
    // 그것은 **틀린 안내다** — 눌러도 소용없고, 되돌릴 수 없는 것을 되돌릴 수 있는 것처럼
    // 보이게 한다. 그리고 이것은 예외가 아니라 **기본 경로**다: 연맹원은 폰으로만 오고 폰
    // 브라우저의 오디오는 기본이 잠김이라, 소리를 한 번도 켜지 않은 사람이 늦게 화면을 열면
    // 언제나 여기다. (2026-08-14 대리 관측이 이 자리를 잡았다.)
    //
    // 노드를 만들기 전에 계산만 먼저 한다. `planSignal` 은 순수 함수라 오디오 상태와 무관
    // 하게 답하고, 「늦었으면 울리지 않는다」의 규칙은 여전히 그 함수 하나가 진다.
    const plan = planSignal({
      audioNowSec: this.#ctx.currentTime,
      startAtSec,
      profile: this.#profile,
    })
    if (plan.kind === 'too-late') return this.#missed(target)

    // **개시 전으로 돌아왔으면 지나감 갈래가 박은 그 대상의 자물쇠를 푼다** (T6 개정 셋째, 2026-10-05). 위 `#keepHonest`
    // 의 지나감 갈래는 동기 안 된 시계가 앞서 있던 틱에서도 자물쇠를 박는다. 시계가 바로잡혀 개시 전으로 돌아오면 그 개시는
    // 아직 오지 않은 것인데, 자물쇠가 남아 있으면 진짜 개시를 놓친 한 건이 「이미 센 것」으로 숨는다(적대 리뷰가 짚었다).
    // 여기는 `planSignal` 이 too-late 가 아니라고 답한 뒤라 지나감 갈래와 같은 부등식의 반대쪽이다. **끈 채 지난 개시와 이미
    // 센 누락의 자물쇠는 풀지 않는다**(2026-10-07): 풀면 끈 구간이 누락이 되고 한 누락이 두 번 세진다.
    if (this.#lockFromPassed && this.#countedMiss !== null && sameTarget(this.#countedMiss, target)) {
      this.#countedMiss = null
      this.#lockFromPassed = false
    }

    // **잠긴 오디오에는 걸지 않는다.** `currentTime` 이 멈춰 있으므로 지금 건 예약은 멈춰
    // 있던 시간만큼 통째로 늦게 울고, 그것이 이 스텝이 없애려는 실패 그 자체다. 잠김을
    // 푸는 것은 예약이 아니라 사람의 제스처이므로(`arm`), 여기서는 말하고 멈춘다.
    //
    // 이 판단이 `scheduleSignal` 이 아니라 여기 있는 이유는 그쪽이 suspended 를 일부러
    // 통과시키기 때문이다 — 오프라인 렌더가 그 상태로 도는 것이 기계 판정의 근거다.
    // 아무것도 들지 않았으므로 다음 틱이 같은 자리에서 다시 시도한다.
    if (this.#ctx.state !== 'running') {
      return this.#settle({
        state: 'locked',
        reason: describeBlockedState(this.#ctx.state),
        droppedMarkers: 0,
      })
    }

    const result: SignalResult = scheduleSignal(this.#ctx, { startAtSec, profile: this.#profile })

    // 위에서 이미 걸렀지만 한 번 더 받는다. 두 호출 사이에 시계가 조금 흐르므로 경계에서
    // 답이 갈릴 수 있고, 그때 답을 내는 쪽은 **실제로 노드를 만든 그 호출**이어야 한다.
    if (result.kind === 'too-late') return this.#missed(target)
    if (result.kind === 'blocked') {
      return this.#settle({ state: 'locked', reason: result.reason, droppedMarkers: 0 })
    }

    this.#held = {
      target,
      binding: { startAtSec, targetOpenMs: target.openMs },
      cancel: result.cancel,
    }
    return this.#settle({ state: 'scheduled', reason: '', droppedMarkers: result.droppedMarkers })
  }

  /**
   * 화면을 떠날 때 걸어 둔 소리를 걷는다.
   *
   * 안 걷으면 다른 화면에서 **아무 이름도 없이** 카운트 소리가 울린다. 「이름 없는
   * 카운트다운은 화면에 존재하지 않는다」의 소리 쪽 짝이다.
   *
   * **영구히 죽이지 않는다.** React 의 StrictMode 가 개발에서 효과를 붙였다 떼었다 다시
   * 붙이므로, 여기서 런타임을 못 쓰게 만들면 개발 중에만 소리가 안 나고 배포에서는 난다 —
   * 그런 차이는 실기기 확인까지 안 드러난다. 들고 있던 것만 놓으므로 다음 `update` 가
   * 아무 일 없었다는 듯 다시 건다.
   */
  dispose(): void {
    this.#release()
    this.#settle(IDLE)
  }

  /**
   * 「늦었으면 울리지 않는다」의 마지막 자리. **침묵만 남기지 않는다** — 소리가 없는 것은
   * 고장과 구별되지 않으므로 화면이 크게 말할 거리를 함께 낸다.
   *
   * 부르는 자리가 둘이라 함수로 뺐다(계산만 한 갈래와 노드를 만든 갈래). 두 곳이 각자
   * 세면 같은 누락이 두 번 찍히고, 그러면 「0건이어야 한다」가 판정할 수 없는 값이 된다.
   */
  #missed(target: SignalTarget): SignalStatus {
    // **전이마다 한 번만 센다.** 이 문은 초당 다섯 번 도는데 「지금 놓친 상태다」를 매번
    // 세면 숫자가 방치한 시간에 비례해 부풀고, 그러면 판정 계약의 「신호 누락 0건 — 이것은
    // 평균이 아니다」가 판정할 수 없는 값 위에 선다. 대상이 바뀌면 **다른 누락**이라 다시 센다.
    if (this.#countedMiss === null || !sameTarget(this.#countedMiss, target)) {
      this.#missedCount += 1
      this.#countedMiss = target
      this.#lockFromPassed = false
    }
    // **얼마나 지났는지는 여기서 말하지 않는다.** 화면이 그 숫자를 이미 큰 글자로 흐르게
    // 적고 있어(「개시 지남 +1:07.7」) 여기서 또 적으면 같은 사실이 두 곳에서, 그것도 더
    // 나쁜 형식으로 뜬다 — 이 자리는 원시 초를 그대로 찍어 「276.5초 전」이 됐고 말투도
    // 화면과 갈렸다. 자르는 규칙은 화면 쪽 형식기 하나가 갖고, 그것을 여기 베끼면 0.1초
    // 격자 규칙이 세 벌이 된다. **왜 안 울리는가**는 i18n 의 본문이 말한다.
    //
    return this.#settle({ state: 'missed', reason: '', droppedMarkers: 0 })
  }

  #release(): void {
    this.#held?.cancel()
    this.#held = null
  }

  #settle(status: SignalStatus): SignalStatus {
    this.#status = status
    return status
  }
}
